import { Box, Grid, styled, Tabs, tabsClasses } from '@mui/material';
import { useCompanyRelativePath } from 'ogp/hooks';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Header,
  LwBlockGridContainer,
  LwButton,
  LwTab,
  Page,
  Toolbox,
  useOnScrollDown,
} from 'redesign';
import { routes } from 'Routes';
import { IconAdd } from 'assets/img';
import { useFeatureFlagContext } from 'shared/contexts/feature-flag-provider';
import { EmployeeCard } from './components/employee-card/employee-card';
import { useGetWorkforce } from './hooks/use-get-workforce';
import { useSelectedWorkforceTab } from './hooks/use-selected-workforce-tab';

const Workforce = () => {
  const { allowBorrowedWorkforce } = useFeatureFlagContext();
  const { selectedTab, handleTabChange } = useSelectedWorkforceTab();
  const workforceQuery = useGetWorkforce(selectedTab);
  const navigate = useNavigate();
  const generatePath = useCompanyRelativePath();

  const { data } = workforceQuery;
  const flatData = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const { fetchIfBottomScrolled } = useOnScrollDown({
    infiniteQueryResult: workforceQuery,
    flatData,
  });

  return (
    <Page
      header={
        <HeaderContainer>
          <Header titleText="Personeelsbestand" />
          <WorkforceTabs value={selectedTab} onChange={handleTabChange} aria-label="tabbladen">
            <LwTab key="own" value="own" label="Eigen medewerkers" />
            {allowBorrowedWorkforce ? (
              <LwTab key="borrowed" value="borrowed" label="Ingeleende medewerkers" />
            ) : null}
          </WorkforceTabs>
        </HeaderContainer>
      }
      toolbox={
        <Toolbox>
          <LwButton
            to={generatePath(routes.EmployeeCreate)}
            startIcon={<IconAdd />}
            color="primary"
            iconColorMode="fill"
          >
            Voeg werknemer toe
          </LwButton>
        </Toolbox>
      }
      onContentScroll={(e) => {
        fetchIfBottomScrolled(e.target as HTMLDivElement);
      }}
    >
      <LwBlockGridContainer container spacing={4}>
        <Grid container spacing={4}>
          {flatData?.map((employee) => (
            <Grid key={employee.id} item xs={6} md={4} lg={3}>
              <EmployeeCard
                key={employee.id}
                {...employee}
                lenderCompanyName={employee.lenderCompanyName ?? undefined}
                onClick={() => {
                  navigate(
                    generatePath(routes.EmployeeDetail, {
                      id: employee.id,
                    })
                  );
                }}
              />
            </Grid>
          ))}
        </Grid>
      </LwBlockGridContainer>
    </Page>
  );
};

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(6),
}));

const WorkforceTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 'unset',
  [`& .${tabsClasses.flexContainer}`]: {
    gap: theme.spacing(8),
  },
}));

export { Workforce };
